import { createSlice } from "@reduxjs/toolkit";
import { DIGITAL_SCREENS } from "../../templates/strings";

const SESSION_KEY = "otp_session";

// ── sessionStorage helpers ────────────────────────────────────────────────────
// Only persist the fields needed to restore the user's place.
// Transient state (errors, loading flags) is intentionally excluded.
const PERSISTED_FIELDS = [
  "screen",
  "leadId",
  "phone",
  "otpVerified",
  "fallbackAt",
  "resendAvailableAt",
  "sessionExpired"
];

const { OTP, ESCALATION, AVAILABILITY, THANKS_DIGITAL, THANKS_MANUAL } =
  DIGITAL_SCREENS;

export function loadOtpSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function saveOtpSession(state) {
  try {
    const toSave = {};
    PERSISTED_FIELDS.forEach((k) => {
      toSave[k] = state[k];
    });
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(toSave));
  } catch {
    // sessionStorage unavailable — silently ignore
  }
}

export function clearOtpSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch (e) {
    // sessionStorage unavailable — silently ignore
  }
}

// ── Initial state — merge with any persisted session on first load ────────────
const defaultState = {
  screen: OTP,
  leadId: null,
  phone: null,
  otpError: null,
  otpVerified: false,
  fallbackAt: null,
  resendAvailableAt: null,
  sessionExpired: false,
  digitalThankyouConfig: null,
  verifyOtpLimitReached: false,
  numberCorrectCount: 0,
  loading: {
    sendOtp: false,
    verifyOtp: false,
    resendOtp: false,
    updatePhone: false,
    manualFallback: false,
    submitAvailability: false,
    sessionStatus: false
  }
};

// Merge persisted session into default state at module load time.
// Loading flags are always reset to false on refresh (never persisted).
const initialState = {
  ...defaultState,
  ...loadOtpSession(),
  loading: { ...defaultState.loading }, // always reset loading flags
  otpError: null // always reset errors
};

const otpSlice = createSlice({
  name: "otp",
  initialState,
  reducers: {
    initSession(state, { payload }) {
      // Only update if not already set (prevents overwriting on refresh)
      if (!state.leadId) state.leadId = payload.leadId;
      if (!state.phone) state.phone = payload.phone;
    },
    restoreSessionTimers(state, { payload }) {
      if (payload.fallbackAt) state.fallbackAt = payload.fallbackAt;
      if (payload.resendAvailableAt)
        state.resendAvailableAt = payload.resendAvailableAt;
    },
    showOtp(state) {
      state.screen = OTP;
      state.otpError = null;
    },
    showEscalation(state) {
      state.screen = ESCALATION;
      state.otpError = null;
    },
    showThanksDigital(state) {
      state.screen = THANKS_DIGITAL;
    },
    showThanksManual(state) {
      state.screen = THANKS_MANUAL;
    },
    markSessionExpired(state) {
      state.sessionExpired = true;
      state.screen = THANKS_MANUAL;
    },
    setOtpError(state, { payload }) {
      state.otpError = payload;
    },
    clearOtpError(state) {
      state.otpError = null;
    },
    otpSendSuccess(state, { payload }) {
      state.fallbackAt = payload.fallbackAt;
      state.resendAvailableAt = payload.resendAvailableAt;
      state.otpError = null;
    },
    otpVerifySuccess(state) {
      state.otpVerified = true;
      state.screen = AVAILABILITY;
      state.otpError = null;
    },
    resendSuccess(state, { payload }) {
      state.resendAvailableAt = payload.resendAvailableAt;
      state.numberCorrectCount = payload.numberCorrectCount;
      state.otpError = null;
      state.screen = OTP;
    },
    setPhone(state, { payload }) {
      state.phone = payload.newPhone;
    },
    setLoading(state, { payload: { key, value } }) {
      state.loading[key] = value;
    },
    setDigitalThankyouConfig(state, { payload }) {
      state.digitalThankyouConfig = payload;
    },
    markVerifyOtpLimitReached(state) {
      state.verifyOtpLimitReached = true;
    }
  }
});

export const {
  initSession,
  restoreSessionTimers,
  showOtp,
  showEscalation,
  showThanksDigital,
  showThanksManual,
  markSessionExpired,
  setOtpError,
  clearOtpError,
  otpSendSuccess,
  otpVerifySuccess,
  resendSuccess,
  setPhone,
  setLoading,
  setDigitalThankyouConfig,
  markVerifyOtpLimitReached
} = otpSlice.actions;

// Selectors
export const selectOtpScreen = (s) => s.otp.screen;
export const selectPhone = (s) => s.otp.phone;
export const selectOtpError = (s) => s.otp.otpError;
export const selectFallbackAt = (s) => s.otp.fallbackAt;
export const selectResendAvailableAt = (s) => s.otp.resendAvailableAt;
export const selectNumberCorrectCount = (s) => s.otp.numberCorrectCount;
export const selectSessionExpired = (s) => s.otp.sessionExpired;
export const selectVerifyOtpLimitReached = (s) => s.otp.verifyOtpLimitReached;
export const selectLoading = (key) => (s) => s.otp.loading[key];

export default otpSlice.reducer;
